import { Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import type { RecordKind } from '../../../../ai/features/record-ref';
import type { CardAction, CardResponse } from '@omni/ai/features/card.schema';
import type { TokenUsage } from '@omni/ai/transports/ai-transport';
import { NovaCardComponent } from '../../nova-card/nova-card.component';
import type { NovaFeedbackEvent } from '../../nova-feedback-footer/nova-feedback-footer.component';

/**
 * Per-record AI summary page: header chrome (eyebrow + label + refresh icon) +
 * skeleton/empty/processed states + the unified Nova card (insight sections,
 * charts, metrics) rendered by `<nova-card>` + Discuss CTA + privacy footer.
 *
 * All card layout / severity color / chip styling lives in `<nova-card>` and its
 * section leaves — this surface only owns the page chrome and the flow states.
 */
@Component({
  selector: 'nova-record-summary',
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule, NovaCardComponent],
  templateUrl: './record-summary.component.html',
  styleUrls: ['./record-summary.component.scss'],
})
export class NovaRecordSummaryComponent {
  @Input() kind: RecordKind = 'contact';
  @Input() label = '';
  @Input() isProcessing = false;
  @Input() lastAnalyzedAt: Date | null = null;

  readonly #summary = signal<CardResponse | null>(null);
  @Input() set summary(value: CardResponse | null) {
    this.#summary.set(value);
  }
  get summaryValue(): CardResponse | null {
    return this.#summary();
  }

  readonly hasSummary = computed<boolean>(() => (this.#summary()?.sections?.length ?? 0) > 0);

  /** Responses API id of the turn that produced `summary`, passed through to `<nova-card>`. */
  @Input() responseId?: string | null = null;
  /** Token usage for the turn that produced `summary`, passed through to `<nova-card>`. */
  @Input() usage?: TokenUsage | null = null;

  /** "Generate" / "Re-generate" the summary against the per-kind agent. */
  @Output() analyze = new EventEmitter<void>();
  /** Switch to the record-scoped follow-up chat sub-view. */
  @Output() openChat = new EventEmitter<void>();
  /** Re-emitted from `<nova-card>` when the rep taps an entity card. */
  @Output() openEntity = new EventEmitter<{ kind: 'contact' | 'account'; id: string }>();
  /** Re-emitted from `<nova-card>` for `kind:'prompt'` action chips. */
  @Output() action = new EventEmitter<CardAction>();
  /** Re-emitted from `<nova-card>`'s feedback footer — a thumbs vote on `summary`. */
  @Output() feedback = new EventEmitter<NovaFeedbackEvent>();

  /** Translation key of the eyebrow that names the kind of record in scope. */
  get kindEyebrowKey(): string {
    switch (this.kind) {
      case 'contact':   return 'NOVA_CONTACT_INSIGHTS';
      case 'account':   return 'NOVA_ACCOUNT_INSIGHTS';
      case 'spotlight': return 'NOVA_TERRITORY_SPOTLIGHT';
    }
  }

  /** Translation key of the empty-state description for the kind of record in scope. */
  get kindDescriptionKey(): string {
    switch (this.kind) {
      case 'contact':   return 'NOVA_CONTACT_INSIGHTS_DESCRIPTION';
      case 'account':   return 'NOVA_ACCOUNT_INSIGHTS_DESCRIPTION';
      case 'spotlight': return 'NOVA_SPOTLIGHT_INSIGHTS_DESCRIPTION';
    }
  }

  get kindIcon(): string {
    switch (this.kind) {
      case 'contact':   return 'person-circle-outline';
      case 'account':   return 'business-outline';
      case 'spotlight': return 'compass-outline';
    }
  }
}
